import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  TrendingUp,
  ArrowBack as ArrowBackIcon, Email as EmailIcon, Phone as PhoneIcon,
  Business as BusinessIcon, AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmLead, LeadStatus, LeadSource } from '../types/crm';

const emptyLead = (): Partial<CrmLead> => ({
  name: '',
  company: '',
  email: '',
  phone: '',
  status: 'NEW',
  source: 'WEBSITE',
  value: 0,
  notes: '',
});

export const LeadsPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [leadsList, setLeadsList] = useState<CrmLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmLead>>(emptyLead());
  const [isEditing, setIsEditing] = useState(false);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const data = await crmApi.getLeads();
      setLeadsList(data);
    } catch (err) {
      console.error('Error fetching CRM leads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const openNew = () => {
    setFormData(emptyLead());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (lead: CrmLead) => {
    setFormData({ ...lead });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (lead: CrmLead) => {
    if (!lead.id && !lead.CLD_IDF) return;
    const leadId = (lead.id || lead.CLD_IDF)!;

    const confirmed = await blobticMessage.confirm({
      message: t('crm.leads.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce prospect ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteLead(leadId);
      if (res.success) {
        blobticMessage.success(t('crm.leads.deleteSuccess', 'Prospect supprimé avec succès.'));
        loadLeads();
      } else {
        blobticMessage.error(res.message || t('crm.leads.deleteError', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.name?.trim()) {
      return t('crm.leads.nameRequired', 'Le nom du prospect est requis.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadLeads();
  };

  const getStatusColor = (status: LeadStatus) => {
    switch (status) {
      case 'NEW': return 'info';
      case 'CONTACTED': return 'warning';
      case 'QUALIFIED': return 'primary';
      case 'PROPOSAL': return 'secondary';
      case 'WON': return 'success';
      case 'LOST': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: LeadStatus) => {
    switch (status) {
      case 'NEW': return t('crm.leads.status.new', 'Nouveau');
      case 'CONTACTED': return t('crm.leads.status.contacted', 'Contacté');
      case 'QUALIFIED': return t('crm.leads.status.qualified', 'Qualifié');
      case 'PROPOSAL': return t('crm.leads.status.proposal', 'Proposition');
      case 'WON': return t('crm.leads.status.won', 'Gagné');
      case 'LOST': return t('crm.leads.status.lost', 'Perdu');
      default: return status || '';
    }
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CLD_IDF: formData.id || formData.CLD_IDF,
      CLD_CODE: formData.code || formData.CLD_CODE,
      CLD_NAME: formData.name,
      CLD_COMPANY: formData.company,
      CLD_EMAIL: formData.email,
      CLD_PHONE: formData.phone,
      CLD_STATUS: formData.status,
      CLD_SOURCE: formData.source,
      CLD_VALUE: formData.value,
      CLD_NOTES: formData.notes,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.leads.editTitle', 'Modifier le Prospect') : t('crm.leads.newTitle', 'Nouveau Prospect')}
        icon={<TrendingUp />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_lead', primaryKey: 'CLD_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyLead())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('crm.leads.informationSection', 'Informations du Prospect')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.leads.field.name', 'Nom complet *')}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.leads.field.company', 'Entreprise')}
                value={formData.company || ''}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label={t('crm.leads.field.email', 'Email')}
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.leads.field.phone', 'Téléphone')}
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.leads.field.status', 'Statut')}
                value={formData.status || 'NEW'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as LeadStatus })}
              >
                <MenuItem value="NEW">{t('crm.status.new', 'Nouveau')}</MenuItem>
                <MenuItem value="CONTACTED">{t('crm.status.contacted', 'Contacté')}</MenuItem>
                <MenuItem value="QUALIFIED">{t('crm.status.qualified', 'Qualifié')}</MenuItem>
                <MenuItem value="PROPOSAL">{t('crm.status.proposal', 'Proposition')}</MenuItem>
                <MenuItem value="WON">{t('crm.status.won', 'Gagné')}</MenuItem>
                <MenuItem value="LOST">{t('crm.status.lost', 'Perdu')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.leads.field.source', 'Source')}
                value={formData.source || 'WEBSITE'}
                onChange={(e) => setFormData({ ...formData, source: e.target.value as LeadSource })}
              >
                <MenuItem value="WEBSITE">{t('crm.source.website', 'Site Web')}</MenuItem>
                <MenuItem value="REFERRAL">{t('crm.source.referral', 'Recommandation')}</MenuItem>
                <MenuItem value="SOCIAL">{t('crm.source.social', 'Réseaux Sociaux')}</MenuItem>
                <MenuItem value="EVENT">{t('crm.source.event', 'Événement / Salon')}</MenuItem>
                <MenuItem value="OTHER">{t('crm.source.other', 'Autre')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('crm.leads.field.value', 'Valeur Estimée (€)')}
                value={formData.value || 0}
                onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('crm.leads.field.notes', 'Notes & Remarques')}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  return (
    <GenericPage
      title={t('crm.leads.pageTitle', 'Gestion des Prospects (Leads)')}
      icon={<TrendingUp />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('crm.leads.newLeadBtn', 'Nouveau Prospect')}
    >
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {leadsList.map((lead) => (
            <Grid item xs={12} sm={6} md={4} key={lead.id || lead.code}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: '#f43f5e', width: 44, height: 44, fontWeight: 700 }}>
                        {lead.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {lead.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lead.code}
                        </Typography>
                      </Box>
                    </Stack>
                    <Chip
                      label={getStatusLabel(lead.status)}
                      color={getStatusColor(lead.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {lead.company && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2">{lead.company}</Typography>
                      </Stack>
                    )}
                    {lead.email && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2">{lead.email}</Typography>
                      </Stack>
                    )}
                    {lead.phone && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2">{lead.phone}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MoneyIcon fontSize="small" sx={{ color: '#059669' }} />
                      <Typography variant="body2" fontWeight={700} color="#059669">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(lead.value || 0)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {t('crm.leads.sourceLabel', 'Source')} : {lead.source || t('crm.leads.sourceDirect', 'Direct')}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" color="primary" onClick={() => openEdit(lead)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(lead)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {leadsList.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('crm.leads.noData', 'Aucun prospect enregistré. Cliquez sur "Nouveau Prospect" pour commencer.')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <GenericTable
          data={leadsList}
          columns={[
            { id: 'code', label: t('crm.leads.col.code', 'Code'), minWidth: 100 },
            { id: 'name', label: t('crm.leads.col.name', 'Nom'), minWidth: 150 },
            { id: 'company', label: t('crm.leads.col.company', 'Entreprise'), minWidth: 150 },
            { id: 'email', label: t('crm.leads.col.email', 'Email'), minWidth: 180 },
            { id: 'phone', label: t('crm.leads.col.phone', 'Téléphone'), minWidth: 130 },
            {
              id: 'value',
              label: t('crm.leads.col.value', 'Valeur'),
              minWidth: 120,
              format: (val: any) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(val) || 0)
            },
            {
              id: 'status',
              label: t('crm.leads.col.status', 'Statut'),
              minWidth: 120,
              format: (val: any) => (
                <Chip
                  size="small"
                  label={getStatusLabel(val)}
                  color={getStatusColor(val) as any}
                />
              )
            },
          ]}
          filterColumns={['name', 'company', 'email', 'status', 'code']}
          searchPlaceholder={t('crm.leads.search', 'Rechercher un prospect...')}
          maxHeight="calc(100vh - 260px)"
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      )}
    </GenericPage>
  );
};

export default LeadsPage;
